import { z } from 'zod'
import { giftablePlans } from './salons.ts'

// Promo codes salons enter in the CRM: at sign-up, when creating the salon or on the billing page.
// A discount waits for the next plan payments; free months gift the plan right away.

export const promoCodeKinds = ['discount', 'free_months'] as const
export const promoCodeKindSchema = z.enum(promoCodeKinds)

export const promoCodeStatuses = ['active', 'inactive', 'expired', 'exhausted'] as const
export const promoCodeStatusSchema = z.enum(promoCodeStatuses)

export const promoRedemptionSources = ['registration', 'business_creation', 'billing'] as const
/** lapsed — held at sign-up, but no salon was created in time or the code was switched off. */
export const promoRedemptionStatuses = ['pending', 'applied', 'lapsed'] as const

/** The limits the Manaro backend enforces. */
// Short codes are easy to guess by trying: the backend wants at least 5 characters.
export const PROMO_CODE_PATTERN = /^[A-Z0-9]{5,32}$/
export const PROMO_CODE_MAX_LENGTH = 32
export const PROMO_DISCOUNT_MAX_PERCENT = 99
export const PROMO_DISCOUNT_MAX_PAYMENTS = 12
export const PROMO_MAX_MONTHS = 12
export const PROMO_MAX_REDEMPTIONS = 100_000
export const PROMO_NOTE_MAX = 500

/** As the backend and the CRM field do: upper case, no spaces or dashes. */
export const normalizePromoCode = (raw: string): string => raw.toUpperCase().replace(/[\s-]/g, '')

const isoDateTime = z.iso.datetime().nullable()

export const promoCodeSchema = z.object({
  id: z.number(),
  code: z.string(),
  kind: promoCodeKindSchema,
  discountPercent: z.number().nullable(),
  discountPayments: z.number().nullable(),
  plan: z.string().nullable(),
  months: z.number().nullable(),
  maxRedemptions: z.number().nullable(),
  used: z.number(),
  expiresAt: isoDateTime,
  isActive: z.boolean(),
  status: promoCodeStatusSchema,
  note: z.string().nullable(),
  createdBy: z.string(),
  createdAt: isoDateTime,
})

export const promoRedemptionSchema = z.object({
  id: z.number(),
  /** null — entered at sign-up, the salon is not created yet. */
  salon: z.object({ id: z.number(), title: z.string() }).nullable(),
  source: z.string(),
  status: z.string(),
  paymentsLeft: z.number().nullable(),
  grantedUntil: isoDateTime,
  appliedAt: isoDateTime,
  createdAt: isoDateTime,
})

export const promoCodeDetailSchema = promoCodeSchema.extend({
  redemptions: z.array(promoRedemptionSchema),
})

export const promoCodesResponseSchema = z.object({
  available: z.boolean(),
  rows: z.array(promoCodeSchema),
})

const codeField = z
  .string({ error: 'Придумайте код.' })
  .transform(normalizePromoCode)
  .pipe(z.string().regex(PROMO_CODE_PATTERN, 'Код — от 5 до 32 латинских букв и цифр.'))

const maxRedemptionsField = z
  .number()
  .int('Лимит — целое число.')
  .min(1, 'Лимит — хотя бы 1.')
  .max(PROMO_MAX_REDEMPTIONS, `Лимит — не больше ${PROMO_MAX_REDEMPTIONS}.`)
  .nullable()

const expiresAtField = z.iso.date({ error: 'Проверьте дату окончания.' }).nullable()

const noteField = z
  .string()
  .trim()
  .max(PROMO_NOTE_MAX, `Не длиннее ${PROMO_NOTE_MAX} символов.`)
  .nullable()

const createCommon = {
  code: codeField,
  maxRedemptions: maxRedemptionsField.default(null),
  expiresAt: expiresAtField.default(null),
  note: noteField.default(null),
}

export const promoCodeCreateRequestSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('discount'),
    ...createCommon,
    discountPercent: z
      .number({ error: 'Укажите скидку в процентах.' })
      .int('Скидка — целое число процентов.')
      .min(1, 'Скидка — от 1%.')
      .max(PROMO_DISCOUNT_MAX_PERCENT, `Скидка — не больше ${PROMO_DISCOUNT_MAX_PERCENT}%. Бесплатно — это «Бесплатные месяцы».`),
    discountPayments: z
      .number({ error: 'Укажите, на сколько оплат.' })
      .int()
      .min(1, 'Хотя бы одна оплата.')
      .max(PROMO_DISCOUNT_MAX_PAYMENTS, `Не больше ${PROMO_DISCOUNT_MAX_PAYMENTS} оплат.`),
  }),
  z.object({
    kind: z.literal('free_months'),
    ...createCommon,
    plan: z.enum(giftablePlans, { error: 'Выберите тариф.' }),
    months: z
      .number({ error: 'Укажите, сколько месяцев.' })
      .int()
      .min(1, 'Хотя бы один месяц.')
      .max(PROMO_MAX_MONTHS, `Не больше ${PROMO_MAX_MONTHS} месяцев.`),
  }),
])

/** Terms stay as they were given out; only availability changes. */
export const promoCodeUpdateRequestSchema = z
  .object({
    isActive: z.boolean().optional(),
    maxRedemptions: maxRedemptionsField.optional(),
    expiresAt: expiresAtField.optional(),
    note: noteField.optional(),
  })
  .refine((request) => Object.values(request).some((value) => value !== undefined), 'Нечего менять.')

export type PromoCodeKind = z.infer<typeof promoCodeKindSchema>
export type PromoCodeStatus = z.infer<typeof promoCodeStatusSchema>
export type PromoCode = z.infer<typeof promoCodeSchema>
export type PromoRedemption = z.infer<typeof promoRedemptionSchema>
export type PromoCodeDetail = z.infer<typeof promoCodeDetailSchema>
export type PromoCodesResponse = z.infer<typeof promoCodesResponseSchema>
export type PromoCodeCreateRequest = z.infer<typeof promoCodeCreateRequestSchema>
export type PromoCodeCreateInput = z.input<typeof promoCodeCreateRequestSchema>
export type PromoCodeUpdateRequest = z.infer<typeof promoCodeUpdateRequestSchema>
